import { FunctionComponent, useEffect } from 'react'
import styled, { css } from 'styled-components'

import { useInView } from 'react-intersection-observer'

import { ellipsis } from 'polished'
import { RecommendedItem } from '@hooks/useRecommendations'
import { formatDuration } from '@widgets/Video/recommendations/utils'

import Blink from '@components/Blink'
import {
  mobileCSS,
  desktopCSS,
  tabletCSS,
  landscapeOrientationCSS,
} from '@measures/responsive'

import TeaserImage from './TeaserImage'
import Gradient from './Gradient'

interface RecommendationsCardProps extends RecommendedItem {
  isActive?: boolean
  smallSize?: boolean
  hidden?: boolean
  onClick?: () => void
  onImpression?: () => void
}

const RecommendationsCardWrapper = styled.div<{
  isActive: boolean
}>`
  position: relative;
  cursor: pointer;
  height: 100%;
  background: ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_50, btvGrey_60 },
      },
    },
    isActive,
  }) => (isActive ? btvGrey_50 : btvGrey_60)};
  border-radius: 4px;

  transition: transform 0.2s;

  &:hover {
    transform: scale(1.04);
  }
`

const StyledBlink = styled(Blink)`
  display: block;
  text-decoration: none;
  height: 100%;
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
`

const DurationBadge = styled.div<Pick<RecommendationsCardProps, 'smallSize'>>`
  ${({ theme, smallSize }) => {
    const mobileStyles = css`
      font-size: 14px;
    `

    const desktopStyles = smallSize
      ? mobileStyles
      : css`
          font-size: 17px;
        `

    const tabletStyles = desktopStyles

    return css`
      position: absolute;
      top: 4px;
      right: 4px;
      padding: 0px 3px;
      border-radius: 2px;

      font-family: ${theme.fonts.sourceSansPro};
      font-style: normal;
      font-weight: normal;
      line-height: 1.25;
      text-transform: uppercase;
      color: ${theme.colors.white};
      background: ${theme.colors.grey.grey_100};

      ${mobileCSS(mobileStyles)}

      ${tabletCSS(tabletStyles)}

      ${desktopCSS(desktopStyles)}
    `
  }}
`

const TitleWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
`

const Title = styled.div<Pick<RecommendationsCardProps, 'smallSize'>>`
  ${({ theme, smallSize }) => {
    const landscapeStyles = landscapeOrientationCSS(css`
      font-size: 20px;

      @media (max-height: 320px) {
        font-size: 17px;
      }
    `)

    const mobileStyles = css`
      font-size: 17px;
      padding: 0 10px;
      margin-bottom: 8px;

      ${landscapeStyles}
    `
    const desktopStyles = smallSize
      ? mobileStyles
      : css`
          font-size: 24px;
          padding: 0 16px;
          margin-bottom: 16px;
        `

    const tabletStyles = smallSize
      ? mobileStyles
      : css`
          ${desktopStyles}
          ${landscapeStyles}
        `

    return css`
      position: relative;
      overflow: hidden;
      font-family: ${theme.fonts.goodOTNarrow};
      font-style: normal;
      font-weight: 800;
      line-height: 1.125;
      letter-spacing: 0.01em;
      text-shadow: 0 0 8px ${theme.colors.blicktvGrey.btvGrey_100},
        0 0 8px ${theme.colors.blicktvGrey.btvGrey_100};
      color: ${theme.colors.blicktvGrey.btvGrey_5};
      word-break: break-word;

      ${ellipsis('100%', 2)};

      ${mobileCSS(mobileStyles)}

      ${tabletCSS(tabletStyles)}

      ${desktopCSS(desktopStyles)}
    `
  }}
`

const RecommendationsCard: FunctionComponent<RecommendationsCardProps> = ({
  title,
  video,
  isActive = false,
  smallSize,
  hidden,
  onClick,
  onImpression,
  image = {
    src: '/incoming/31574-v2-missing.png',
    alt: 'image not found',
  },
  link,
  aureusOfferId,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.5,
  })

  useEffect(() => {
    if (!hidden && inView) {
      onImpression?.()
    }
  }, [inView, hidden, onImpression])

  const clickableProps = {
    ...link,
    aureusOfferId,
    onClick,
    ...(link?.title ? { ariaLabel: link.title } : {}),
  }

  return (
    <RecommendationsCardWrapper ref={ref} isActive={isActive}>
      <StyledBlink {...clickableProps}>
        <ImageWrapper>
          <TeaserImage
            src={image?.src}
            alt={image?.alt}
            availableCrops={image?.crops}
            smallSize={smallSize}
          />
          <DurationBadge smallSize={smallSize}>
            {formatDuration(video?.duration)}
          </DurationBadge>
        </ImageWrapper>
        <TitleWrapper>
          <Gradient />
          <Title
            smallSize={smallSize}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
        </TitleWrapper>
      </StyledBlink>
    </RecommendationsCardWrapper>
  )
}

export default RecommendationsCard
